/**
 * Every read the clients panel makes, as `queryOptions` — and, unlike the frozen roster spike,
 * with its keys **exported**.
 *
 * That is the point of this file. The old `pages/clients/api/rosterQueries.ts` kept its keys
 * private with the note "nothing invalidates these yet", which was true of a read-only screen and
 * stopped being true the moment the panel grew writes: inviting a client changes the roster AND
 * the seat count, ending a coaching relationship changes the roster AND the seats AND the archive,
 * and accepting a request changes the roster AND the request list. A mutation that cannot name the
 * cache it invalidated leaves the trainer looking at a list that disagrees with what they just did.
 *
 * `clientRoot` is the blanket. Every key below descends from it, so a write whose blast radius is
 * genuinely "the whole panel" invalidates one prefix rather than enumerating five.
 *
 * No repository and no service: TanStack Query already owns async state, and CLAUDE.md puts query
 * keys and options in the slice's `api/` segment.
 */

import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import {
  getAttentionQueue,
  getAttentionQueuePreferences,
} from '~/shared/api/generated/endpoints/attention-queue/attention-queue';
import { getVitalsCatalog } from '~/shared/api/generated/endpoints/vitals/vitals';
import type { GetAttentionQueueParams } from '~/shared/api/generated/model';
import {
  getCustomer,
  getCustomerArchived,
  getCustomerId,
  getCustomerIdBodyHistory,
  getCustomerIdCalendar,
  getCustomerIdDetail,
  getCustomerIdGoal,
  getCustomerIdNote,
  getCustomerInvitations,
  getCustomerSeatUsage,
} from '~/shared/api/generated/endpoints/customer/customer';
import { contentLanguage, type ContentLanguage } from '~/shared/i18n/contentLanguage';

/** The prefix every client read shares. Invalidate this to drop the whole panel's cache. */
export const clientRoot = ['clients'] as const;

/**
 * `includeMetrics=true` is the whole reason the roster can carry attendance, a goal and a plan
 * name without a second request per row. Without it the response omits seven keys and four of the
 * canon's five columns have nothing to show.
 */
const ROSTER_PARAMS = { includeMetrics: true } as const;

/**
 * The PREFIX every roster read shares, and the key every roster write invalidates.
 *
 * The zone the read was asked in rides at the TAIL, added by `rosterQueryOptions` and not here —
 * the same construction `attentionQueueQueryKey` uses one block down, and for the same reason: a
 * write that changes the roster has to drop every zone's cached answer, and a prefix
 * partial-matches all of them.
 */
export const rosterQueryKey = [...clientRoot, 'roster', ROSTER_PARAMS] as const;

/**
 * The roster.
 *
 * `timeZoneId` joined the request on 15 September 2026, when the backend learned to answer two
 * counters the trainer's home draws: `activeLast7Days` — did this client finish anything in the
 * last seven of THEIR days (R10, R21) — and `weekComplianceDone/Total`, this week's sessions where
 * the WEEK is the trainer's and the day is the client's. Both are windows with edges, and an
 * omitted zone puts those edges on the server's business default: a trainer in Auckland reading on
 * Monday morning would be shown last week.
 *
 * It rides at the tail of the key for the reason the attention queue's does: the answer belongs to
 * the zone it was asked in, and two zones must not collide on one cache entry.
 *
 * Read once per CALL rather than at module scope — under the React Compiler that is once per mount
 * of the screen, which is when a zone change can actually be picked up.
 */
export function rosterQueryOptions() {
  const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const params = { ...ROSTER_PARAMS, timeZoneId };
  return queryOptions({
    queryKey: [...rosterQueryKey, timeZoneId] as const,
    queryFn: ({ signal }) => getCustomer(params, undefined, signal),
  });
}

/**
 * The PREFIX every attention-queue read shares, and the key every mutation invalidates.
 *
 * The zone the read was asked in is appended by `attentionQueueQueryOptions`, not here, and
 * deliberately: a write that changes the queue has to drop every zone's cached answer, not only
 * the one the trainer happens to be in at that moment. Kept as a prefix, `invalidateQueries`
 * partial-matches all of them.
 */
export const attentionQueueQueryKey = [...clientRoot, 'attention-queue'] as const;

/**
 * The generation of the reason catalogue this build can draw (attention-queue R26): low sleep, low
 * energy and the personal record. A read that declares nothing is answered with the catalogue as it
 * stood before generations existed, so none of the three would arrive.
 */
const REASON_SET = 1;

/**
 * The attention queue.
 *
 * `timeZoneId` is resolved from the browser rather than hardcoded: the backend uses it to decide
 * what "yesterday" and "this week" mean for the day-boundary detectors, and a trainer travelling
 * would otherwise get a queue built against the server's idea of midnight.
 *
 * It therefore rides at the TAIL of the key as well as in the request. The response belongs to the
 * zone it was asked in, so two zones must not collide on one cache entry: a trainer who crosses a
 * boundary (or whose OS zone changes) would otherwise keep being served the previous zone's queue.
 *
 * How fresh that read is has a floor worth knowing. Under React Compiler this call memoizes with no
 * dependencies, so the zone is re-read once per MOUNT of the screen, not on every render: a zone that
 * changes while the tab stays mounted is picked up when the trainer navigates back to it, not before.
 * Function scope is still strictly better than module scope, which would read it once per page load.
 *
 * `retry: 0` overrides the app-wide single retry on purpose. This read is rate-limited to 30
 * requests a minute per user, and its failure is survivable — the roster renders without the task
 * count and without the risk lines. Spending a second attempt to recover those costs the trainer a
 * slower first paint on the read that is *not* survivable, since both are in flight together.
 */
export function attentionQueueQueryOptions() {
  const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // A record names its exercise in the reader's CONTENT language (R24), so the read carries it and
  // the two languages' answers keep separate cache entries. Read per call, like the zone above, and
  // with the same floor: a language switched on `/konto` is picked up when a queue screen mounts.
  const lang = contentLanguage();
  // `includeLeadItems` opts this app into the public form's `PublicFormLeadSubmitted` items
  // (public-form R20). The phone never sends it, so the reason never takes a slot in its queue.
  const params: GetAttentionQueueParams = {
    timeZoneId,
    includeLeadItems: true,
    reasonSet: REASON_SET,
    lang,
  };
  return queryOptions({
    queryKey: [...attentionQueueQueryKey, timeZoneId, lang] as const,
    queryFn: ({ signal }) => getAttentionQueue(params, undefined, signal),
    retry: 0,
  });
}

/**
 * The trainer's task preferences (attention-queue R16, R25): which kinds reach the queue and, for
 * low sleep and low energy, the level in force and its default.
 *
 * Not under the queue's root: it is a different read with a different life. Moved here from
 * `pages/tasks` on 2 October 2026, when `lib/useWellbeingLevels` became its second reader — the
 * kinds screen writes this entry optimistically, and every chart and column that paints "low" reads
 * the same one, so a level changed there repaints them without a request.
 */
export const attentionPreferencesQueryKey = ['attention-queue', 'preferences'] as const;

export function attentionPreferencesQueryOptions() {
  return queryOptions({
    queryKey: attentionPreferencesQueryKey,
    queryFn: ({ signal }) => getAttentionQueuePreferences(undefined, signal),
  });
}

/**
 * The kinds of reading kept about a client — unit, default low level and the levels a trainer may
 * set (R25). Shipped in the server's code and the same for every caller, so it is read once a
 * session.
 */
export function vitalsCatalogQueryOptions() {
  return queryOptions({
    queryKey: ['vitals', 'catalog'] as const,
    queryFn: ({ signal }) => getVitalsCatalog(undefined, signal),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export const seatUsageQueryKey = [...clientRoot, 'seat-usage'] as const;

/**
 * How many seats the trainer's plan carries and how many are spoken for.
 *
 * Read by the invite dialog before it will let a form be submitted, and invalidated by every write
 * that can take or release a seat — inviting, archiving, accepting a request.
 */
export function seatUsageQueryOptions() {
  return queryOptions({
    queryKey: seatUsageQueryKey,
    queryFn: ({ signal }) => getCustomerSeatUsage(undefined, signal),
  });
}

export const archivedClientsQueryKey = [...clientRoot, 'archived'] as const;

/**
 * Ended coaching relationships, keyset-paged.
 *
 * The cursor is a pair — `endedAt` plus `id` — because `endedAt` alone is not unique and a page
 * boundary landing between two relationships ended in the same transaction would either repeat a
 * row or skip one. `hasMore` is the backend's own flag; when it is false `getNextPageParam` returns
 * `undefined` and TanStack stops offering "load more".
 */
export function archivedClientsQueryOptions() {
  return infiniteQueryOptions({
    queryKey: archivedClientsQueryKey,
    queryFn: ({ pageParam, signal }) => getCustomerArchived(pageParam, undefined, signal),
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore !== true) return undefined;
      if (lastPage.nextCursorEndedAt == null || lastPage.nextCursorId == null) return undefined;
      return { cursorEndedAt: lastPage.nextCursorEndedAt, cursorId: lastPage.nextCursorId };
    },
  });
}

export const coachingRequestsQueryKey = [...clientRoot, 'coaching-requests'] as const;

/**
 * People who have asked to be coached by this trainer, keyset-paged on `createdAt` plus `id` for
 * the same reason the archive pages on `endedAt` plus `id`.
 */
export function coachingRequestsQueryOptions() {
  return infiniteQueryOptions({
    queryKey: coachingRequestsQueryKey,
    queryFn: ({ pageParam, signal }) => getCustomerInvitations(pageParam, undefined, signal),
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore !== true) return undefined;
      if (lastPage.nextCursorCreatedAt == null || lastPage.nextCursorId == null) return undefined;
      return { cursorCreatedAt: lastPage.nextCursorCreatedAt, cursorId: lastPage.nextCursorId };
    },
  });
}

/* ── One client, read by two surfaces ─────────────────────────────────────────────────────────
 *
 * The four reads below were `pages/client-detail/api/clientDetailQueries.ts`'s until 31 August
 * 2026, when the roster's KANON v2-D preview panel became their second consumer. They are here for
 * the reason CLAUDE.md gives for promoting anything: **the second real consumer**, and the fact
 * that two copies of a query key are two caches for one fact. The roster panel would otherwise
 * re-fetch a client the trainer had just hovered the moment they opened them, and a key changed on
 * one side would split the two caches silently.
 *
 * Everything else about one client — measurements, records, trends, surveys, muscle volume,
 * consistency — stays in `pages/client-detail`, because nothing else reads it. This is the promotion
 * rule applied, not a layer for its own sake.
 */

/** Every key for one client descends from here, so "forget this client" is one prefix. */
export function clientDetailRoot(clientId: number) {
  return [...clientRoot, 'detail', clientId] as const;
}

export function clientDetailQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'header'] as const,
    queryFn: ({ signal }) => getCustomerIdDetail(clientId, undefined, signal),
  });
}

/**
 * **Who a client IS, and nothing else** — the name, the two letters and the photograph.
 *
 * The SAME cache entry `clientDetailQueryOptions` above owns: one key, one request, one answer.
 * The `select` is what makes it a different READING of it rather than a second read, and it is
 * declared here by name rather than inline at a call site, because TanStack memoises a `select` on
 * identity and an inline arrow re-projects on every render.
 *
 * Promoted on its second real consumer, 19 September 2026: the plan calendar's own screen draws the
 * client as a quiet identity beside its back chevron, and the client detail's top bar draws the
 * same three fields. The detail keeps its own fuller projection (`buildSummaryColumn`'s
 * `IdentityView` carries the status word and the "since" line too, which no other surface wants);
 * what is shared is the READ, which is where the cost and the disagreement both live.
 *
 * `undefined` while the read is open or refused — the caller reserves the identity's box and draws
 * nothing in it, which is the loading policy's own rule.
 */
export interface ClientIdentity {
  readonly name: string;
  /** Composed by the BACKEND (`person.initials`), so every surface draws the same two letters. */
  readonly initials: string;
  readonly avatarUrl: string | null;
}

export function clientIdentityQueryOptions(clientId: number) {
  return queryOptions({
    ...clientDetailQueryOptions(clientId),
    select: toClientIdentity,
  });
}

function toClientIdentity(dto: {
  person?: { fullName?: string | null; initials?: string | null; avatarUrl?: string | null } | null;
}): ClientIdentity {
  return {
    name: dto.person?.fullName ?? '',
    initials: dto.person?.initials ?? '',
    avatarUrl: dto.person?.avatarUrl ?? null,
  };
}

export function clientNoteQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'note'] as const;
}

export function clientNoteQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: clientNoteQueryKey(clientId),
    queryFn: ({ signal }) => getCustomerIdNote(clientId, undefined, signal),
  });
}

/**
 * The backend's own maximum, and what the Pomiary tab reads. Here since 26 September 2026, when the
 * weight goal's task card became the history's second reader (one cache tree for one client).
 *
 * It was 60 while the tab drew a history list and a short series. Direction D compares two dates
 * anywhere in the client's history and offers "od startu", so the read asks for everything the
 * contract will give: `limit` is validated 1..200 and the rows dropped past the cap are the
 * OLDEST. Exported because a read that came back AT the cap cannot claim to have loaded the start —
 * the Pomiary tab's `model/twoDates` relabels the preset with the oldest date it actually holds.
 *
 * A client past 200 snapshots still cannot reach their first measurement through any endpoint.
 * Naming it needs an additive field on `/body/history` and is a follow-up, not this round's.
 */
export const BODY_HISTORY_LIMIT = 200;

export function clientBodyHistoryQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'body-history'] as const;
}

/**
 * The measurement history.
 *
 * The Pomiary tab's chart reads its series from HERE rather than from `/body`, and that is a
 * contract gap made visible: `/body`'s `trend` carries weight and body fat only, so waist, hips and
 * chest — three of the canon's five switch positions — have no weekly series at all. Each history
 * entry is a full snapshot with its own `recordedAt`, which is the honest source for all five.
 */
export function clientBodyHistoryQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: [...clientBodyHistoryQueryKey(clientId), BODY_HISTORY_LIMIT] as const,
    queryFn: ({ signal }) =>
      getCustomerIdBodyHistory(clientId, { limit: BODY_HISTORY_LIMIT }, undefined, signal),
  });
}

export function clientGoalQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'goal'] as const;
}

export function clientGoalQueryOptions(clientId: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: [...clientGoalQueryKey(clientId), lang] as const,
    queryFn: ({ signal }) => getCustomerIdGoal(clientId, { lang }, undefined, signal),
  });
}

/**
 * The client's own profile row — read for fields `CustomerDetailResponse` does not carry.
 *
 * Its one consumer today is the Trendy tab's silhouette, which needs a sex the detail read has no
 * field for. Here rather than in that page only because it shares the prefix above.
 */
export function clientProfileQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'profile'] as const,
    queryFn: ({ signal }) => getCustomerId(clientId, undefined, signal),
  });
}

/**
 * One client's sessions across a window, with the server's derived `uiStatus` on every row.
 *
 * ── Why the window is UTC instants and not `DateOnly` ────────────────────────────────────────
 * `CustomerProcessor.GetCalendarAsync` takes `DateTime?`s and normalises them through
 * `DateTimeUtc.Ensure`, which reinterprets an unspecified kind as UTC rather than refusing it —
 * unlike `/calendarevent/.../schedule`, which answers 400 for a bare date. So a full ISO instant is
 * what is sent: it is unambiguous under both readings, and the window is a rolling one anyway.
 *
 * `tz` rides along because the SERVER groups and labels the timeline in it. The panel reads none of
 * those labels, but a request that omits the zone gets the business default, and the same response
 * is what a future consumer of the labels would receive — so the zone is at the tail of the key
 * too, and a cached answer is never handed to a different one.
 *
 * The server's own default window is ninety days back and thirty forward. This asks for the days it
 * draws and nothing else; the projection folds whatever it is handed, and the WINDOW is a property
 * of the request.
 */
export function clientCalendarWindowQueryOptions(
  clientId: number,
  range: { from: string; to: string },
) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return queryOptions({
    queryKey: [...clientDetailRoot(clientId), 'calendar-window', range, tz] as const,
    queryFn: ({ signal }) =>
      getCustomerIdCalendar(clientId, { ...range, order: 'asc', tz }, undefined, signal),
  });
}
